<template>
  <section
    ref="sectionRef"
    class="kits-showcase-with-filters mb-10 py-6 md:py-8"
  >
    <div class="max-w-screen-xl mx-auto w-full px-4">
      <Heading
        v-if="title"
        :title="title"
        :level="1"
        class="page-title"
        h-class="text-h4 md:text-h2 font-semibold text-teal-900 mb-2"
      />
      <p v-if="subtitle" class="mb-6 max-w-3xl text-base text-gray-600">
        {{ subtitle }}
      </p>

      <div ref="filterPillsRef" class="filter-wrap">
        <button
          v-for="category in categoriesWithSlug"
          :key="category.id"
          type="button"
          class="rounded-full border px-4 py-2 text-sm whitespace-nowrap transition-colors"
          :class="
            categorySlug === category.categorySlug
              ? 'border-teal-700 bg-teal-50 text-teal-900'
              : 'border-gray-200 text-teal-900 hover:border-teal-600'
          "
          @click="selectCategory(category.categorySlug!)"
        >
          {{ category.title }}
        </button>
        <div class="border-l border-gray-200 h-8 shrink-0"></div>
        <button
          type="button"
          class="inline-flex shrink-0 items-center gap-1 rounded-full border px-4 py-2 text-sm font-normal whitespace-nowrap transition-colors [&>svg]:fill-[#FF6A39]"
          :class="
            saleOnly
              ? 'bg-[#FFFCF3] !border-[#FFE1D4] text-[#C7491F]'
              : 'border-gray-200 text-teal-900 hover:bg-[#FFFCF3] hover:!border-red-700'
          "
          :aria-pressed="saleOnly"
          @click="toggleSale"
        >
          <IconBolt class="h-5 w-5" aria-hidden="true" />
          {{ t('kits.index.saleFilter') }}
        </button>

        <details
          ref="priceDetailsRef"
          class="relative ml-auto shrink-0"
          @toggle="onPriceDetailsToggle"
        >
          <summary
            ref="priceSummaryRef"
            class="inline-flex cursor-pointer list-none items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm text-teal-900 hover:border-teal-600 [&::-webkit-details-marker]:hidden"
          >
            <ListFilter class="h-4 w-4 shrink-0" aria-hidden="true" />
            {{ t('kits.index.moreFilters') }}
            <IconChevronDown class="h-3 w-3 shrink-0" aria-hidden="true" />
          </summary>
        </details>

        <Teleport to="body">
          <div
            v-if="pricePanelOpen"
            ref="pricePanelRef"
            class="fixed z-[100] rounded-xl border border-gray-200 bg-white p-4 shadow-lg"
            :style="pricePanelStyle"
          >
            <FilterItemPriceRange
              :price-min="priceMinEur ?? KITS_PRICE_SLIDER_MIN"
              :price-max="priceMaxEur ?? KITS_PRICE_SLIDER_MAX"
              @updated="onPriceRangeUpdated"
            />
          </div>
        </Teleport>
      </div>

      <p class="mb-4 text-xs text-gray-500">
        {{ t('kits.index.resultsCount', { count: totalHits }) }}
      </p>

      <ProductShowcase
        :products="products"
        :loading="pending"
        :use-h-tag="false"
        :is-mobile-slider="false"
        :is-duration-visible="false"
        :gtm-item-list-id="GtmItemListId.KitsPageKits"
        algolia-insights-label="Kits - showcase with filters"
        data-test-id="kits-showcase-with-filters-showcase"
        image-loading="lazy"
        class="mt-2"
      />

      <FilterPagination
        v-if="totalPages > 1"
        :current-page="page"
        :hits-per-page="perPage"
        :nb-hits="totalHits"
        :nb-pages="totalPages"
        @updated="onPaginate"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { onClickOutside, useEventListener } from '@vueuse/core'
import { ListFilter } from 'lucide-vue-next'
import { toast, type ToastOptions } from 'vue3-toastify'
import IconBolt from '~/assets/icons/search-filters/bolt-stroke.svg?component'
import IconChevronDown from '~/assets/icons/chevron-down.svg?component'
import Heading from '~/components/common/Heading.vue'
import FilterItemPriceRange from '~/components/filter/FilterItemPriceRange.vue'
import FilterPagination from '~/components/filter/FilterPagination.vue'
import ProductShowcase from '~/components/product/ProductShowcase.vue'
import { GtmItemListId } from '~/composables/useKftGtm/types'
import { useKitsBrowseProducts } from '~/composables/useKitsBrowseProducts'
import type { StrapiKitsListSummary } from '~/composables/useStrapiKitsListSummaries'

/** Must match slider range in FilterItemPriceRange */
const KITS_PRICE_SLIDER_MIN = 0
const KITS_PRICE_SLIDER_MAX = 1200

const { t } = useI18n()

const {
  title = '',
  subtitle = '',
  categories,
} = defineProps<{
  title?: string
  subtitle?: string
  categories: StrapiKitsListSummary[]
}>()

const categoriesWithSlug = computed(() =>
  categories.filter(
    (c): c is StrapiKitsListSummary & { categorySlug: string } =>
      Boolean(c.categorySlug)
  )
)

const {
  products,
  totalHits,
  totalPages,
  pending,
  error,
  page,
  perPage,
  categorySlug,
  saleOnly,
  priceMinEur,
  priceMaxEur,
} = useKitsBrowseProducts()

function selectCategory(slug: string) {
  categorySlug.value = categorySlug.value === slug ? null : slug
}

const priceDetailsRef = ref<HTMLDetailsElement | null>(null)
const priceSummaryRef = ref<HTMLElement | null>(null)
const pricePanelRef = ref<HTMLElement | null>(null)
const filterPillsRef = ref<HTMLElement | null>(null)
const pricePanelOpen = ref(false)

const panelMetrics = ref({ top: 0, left: 0, width: 320 })

function updatePricePanelPosition() {
  if (!import.meta.client) return
  const summary = priceSummaryRef.value
  if (!summary) return
  const r = summary.getBoundingClientRect()
  const gutter = 16
  const gap = 8
  const maxWidth = Math.min(320, window.innerWidth - gutter * 2)
  const width = maxWidth
  let left = r.right - width
  if (left < gutter) left = gutter
  if (left + width > window.innerWidth - gutter) {
    left = Math.max(gutter, window.innerWidth - width - gutter)
  }
  panelMetrics.value = {
    top: r.bottom + gap,
    left,
    width,
  }
}

const pricePanelStyle = computed(() => {
  const m = panelMetrics.value
  return {
    top: `${Math.round(m.top)}px`,
    left: `${Math.round(m.left)}px`,
    width: `${Math.round(m.width)}px`,
    maxWidth: 'calc(100vw - 2rem)',
  }
})

function onPriceDetailsToggle(e: Event) {
  const target = e.target as HTMLDetailsElement
  pricePanelOpen.value = target.open
  if (target.open) {
    nextTick(() => updatePricePanelPosition())
  }
}

onClickOutside(
  priceDetailsRef,
  () => {
    const el = priceDetailsRef.value
    if (el?.open) {
      el.open = false
    }
  },
  { ignore: [pricePanelRef] }
)

if (import.meta.client) {
  useEventListener(
    window,
    'scroll',
    () => {
      if (pricePanelOpen.value) updatePricePanelPosition()
    },
    { capture: true, passive: true }
  )
  useEventListener(window, 'resize', () => {
    if (pricePanelOpen.value) updatePricePanelPosition()
  })
}

useEventListener(filterPillsRef, 'scroll', () => {
  if (pricePanelOpen.value) updatePricePanelPosition()
})

function toggleSale() {
  saleOnly.value = !saleOnly.value
}

function onPriceRangeUpdated({
  priceMin,
  priceMax,
}: {
  priceMin: number
  priceMax: number
}) {
  if (
    priceMin === KITS_PRICE_SLIDER_MIN &&
    priceMax === KITS_PRICE_SLIDER_MAX
  ) {
    priceMinEur.value = undefined
    priceMaxEur.value = undefined
  } else {
    priceMinEur.value = priceMin
    priceMaxEur.value = priceMax
  }
}

const sectionRef = ref<HTMLElement | null>(null)

function onPaginate(nextPage: number) {
  page.value = nextPage
  nextTick(() => {
    sectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

watch(error, (e) => {
  if (!e) return
  toast(t('general.error'), {
    autoClose: 2000,
    type: 'error',
    position: toast.POSITION.TOP_CENTER,
    multiple: false,
  } as ToastOptions)
})
</script>

<style lang="postcss" scoped>
.filter-wrap {
  @apply flex flex-wrap items-center gap-2 py-2 max-md:flex-nowrap max-md:overflow-x-auto max-md:overflow-y-visible;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.page-title {
  :deep(h1) {
    @apply text-h3 md:text-h2 font-semibold text-teal-900 mb-2;
  }
}
</style>
